import { useEffect, useRef, useState } from 'react';
import { Query, QueryOptions } from '../useQuery';
import { Request, RequestOptions } from '../useRequest';
import useGetSitesNonAdmin from '../../Requests/useGetSitesNonAdmin';
import { KaSelectOption } from '../KaSelect';

export type ConnectorSyncState = 'idle' | 'fetching' | 'polling' | 'processing' | 'error';

const POLL_INTERVAL_MS = 2000;

export interface TmxFetchResponse {
  notificationId: string;
}

export interface ConnectorSyncStatus {
  complete: boolean;
  error: string | null;
}

export interface ConnectorSyncSpec {
  useTmxFetch: (siteId: string, options?: RequestOptions<TmxFetchResponse>) => Request<TmxFetchResponse, void>;
  useStatus: (siteId: string, notificationId: string, options?: QueryOptions<ConnectorSyncStatus>) => Query<ConnectorSyncStatus>;
}

interface ConnectorSyncOptions {
  onFetchError?: (message: string) => void;
  onSiteChange?: () => void;
}

export interface ConnectorSync {
  state: ConnectorSyncState;
  selectedSiteId: string;
  selectedOption: KaSelectOption | null;
  siteOptions: KaSelectOption[];
  statusError: string | null;
  selectSite: (siteId: string) => void;
  startFetch: () => void;
  retry: () => void;
}

const useConnectorSync = (spec: ConnectorSyncSpec, options?: ConnectorSyncOptions): ConnectorSync => {
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [state, setState] = useState<ConnectorSyncState>('idle');
  const [notificationId, setNotificationId] = useState('');
  const [statusError, setStatusError] = useState<string | null>(null);

  const getSites = useGetSitesNonAdmin();
  const siteOptions: KaSelectOption[] = (getSites.data ?? [])
    .filter(s => s.isRegistered)
    .map(s => ({ value: s.id, label: s.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const selectedOption = siteOptions.find(o => o.value === selectedSiteId) ?? null;

  const tmxFetch = spec.useTmxFetch(selectedSiteId, {
    onSuccess: data => {
      setNotificationId(data.notificationId);
      setState('polling');
    },
    onError: err => {
      options?.onFetchError?.(err.message || 'Failed to fetch from the connector.');
      setState('error');
    },
  });

  const statusQuery = spec.useStatus(selectedSiteId, notificationId, {
    autoQuery: false,
    onSuccess: data => {
      if (data.error) {
        setStatusError(data.error);
      } else if (data.complete) {
        setStatusError(null);
        setState('processing');
      } else {
        setStatusError(null);
      }
    },
    onError: () => setStatusError('Failed to retrieve sync status.'),
  });

  const statusQueryRef = useRef(statusQuery.query);
  useEffect(() => { statusQueryRef.current = statusQuery.query; });

  useEffect(() => {
    if (state !== 'polling') return;
    const interval = setInterval(() => statusQueryRef.current(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [state]);

  const selectSite = (siteId: string) => {
    setSelectedSiteId(siteId);
    setState('idle');
    setNotificationId('');
    setStatusError(null);
    options?.onSiteChange?.();
  };

  const startFetch = () => {
    setStatusError(null);
    setState('fetching');
    tmxFetch.request();
  };

  const retry = () => {
    setStatusError(null);
    setState('idle');
  };

  return {
    state,
    selectedSiteId,
    selectedOption,
    siteOptions,
    statusError,
    selectSite,
    startFetch,
    retry,
  };
};

export default useConnectorSync;
